import React, { useEffect, useState } from 'react'
import { useTranslation } from 'language/translator'

import MoleculesTable from 'components_new/molecules/Table'
import {
  useFilteredAndSortedWidgetData,
  useTransformedTableData,
  WidgetTableRow
} from './hooks'

import {
  getActiveOrder,
  getActiveOrderBy,
  getExpandedPathSet,
  getSegmentIndexOrder
} from './utils'

import {
  FormattedWidgetData,
  WidgetOrder,
  TWidgetSettings,
  FormattedWidgetDataDataset
} from 'types/GlobalWidget'

interface WidgetTableProps {
  datasets: FormattedWidgetDataDataset[]
  formattedData: FormattedWidgetData
  isComparativeButIllegal: boolean
  onCellClick?: (
    row: WidgetTableRow,
    event: React.MouseEvent<HTMLElement>
  ) => void
  onSort?: (id: string, index: number | null) => void
  order: WidgetOrder
  scaleFactor: number
  settings: TWidgetSettings
  showLinks: boolean
}

/**
 * Core Table component for displaying widget data in table format.
 *
 * This component:
 * 1. Calls useFilteredAndSortedWidgetData with widget-specific params
 * 2. Translates WidgetTableRow[] to generic TableRow[]
 * 3. Passes clean, generic data to the Table molecule
 * 4. Delegates cell click and sort handling to optional callbacks
 */
const WidgetTable = (props: WidgetTableProps) => {
  const {
    datasets,
    formattedData,
    isComparativeButIllegal,
    onCellClick,
    onSort,
    order,
    scaleFactor,
    settings,
    showLinks
  } = props

  const { t } = useTranslation()
  // rows the user has opened or closed, by path
  const [expandOverrides, setExpandOverrides] = useState<Map<string, boolean>>(
    new Map()
  )

  // a new setting shows its own starting point rather than the clicks
  // made under the previous one
  useEffect(() => {
    setExpandOverrides(new Map())
  }, [settings.expanded_segment_levels])

  const expandedPathSet = getExpandedPathSet(
    formattedData.labels,
    settings.expanded_segment_levels,
    expandOverrides
  )

  const handleToggleExpanded = (path: string, expanded: boolean) => {
    setExpandOverrides((prev) => new Map(prev).set(path, expanded))
  }

  // with variant options the rows are metrics, not segment values
  const hasVariantOptions =
    formattedData.expandableMetricRows && settings.variant_options.length > 0

  // Calculate active order/orderBy: prioritizes a manual click, otherwise
  // falls back to the widget's configured default sort (settings.sort_by).
  // These only render the header arrow - the rows arrive from the backend
  // already in the configured default order, so the hook below sorts on
  // the client only when a manual order (a header click) is set.
  const activeOrder = getActiveOrder(order, settings)
  const activeOrderBy = getActiveOrderBy(order, settings, datasets)

  const { rows: widgetRows, columns } = useFilteredAndSortedWidgetData({
    formattedData,
    expandedPathSet,
    order,
    horizontal: settings.horizontal,
    showLinks,
    showTotal: settings.show_total,
    segmentIndexOrder: getSegmentIndexOrder(settings)
  })

  // Transform widget data to generic Table molecule format
  const { genericColumns, genericRows } = useTransformedTableData({
    columns,
    datasets,
    formattedData,
    isComparativeButIllegal,
    onCellClick,
    settings,
    widgetRows
  })

  return (
    <MoleculesTable
      activeOrder={activeOrder}
      activeOrderBy={activeOrderBy}
      columns={genericColumns}
      expandedPathSet={expandedPathSet}
      horizontal={settings.horizontal}
      onSort={onSort}
      rows={genericRows}
      scaleFactor={scaleFactor}
      segmentByName={
        hasVariantOptions
          ? settings.segment_by.display_name ??
            t('widgets', 'variant_options_row_header')
          : settings.segment_by.label ?? ''
      }
      segmentMaxChars={settings.label_chars}
      onToggleExpanded={handleToggleExpanded}
      showHeader={settings.show_header}
      showTotal={settings.show_total}
      showVerticalDividers={settings.show_vertical_dividers}
    />
  )
}

export default WidgetTable
